/**
 * Central source of truth for OpenAI model options
 */

export interface AIModelOption {
  value: string
  label: string
}

/**
 * Complete list of available AI models
 * Use this as the default for most model selection dropdowns
 */
export const AI_MODELS: AIModelOption[] = [
  { value: 'gpt-6-luna', label: 'GPT-6 Luna' },
  { value: 'gpt-6-sol', label: 'GPT-6 Sol' },
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna' },
  { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol' },
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
  { value: 'gpt-5.4', label: 'GPT-5.4' },
  { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini' },
  { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano' },
  { value: 'gpt-5.2', label: 'GPT-5.2' },
  { value: 'gpt-5.1', label: 'GPT-5.1' },
  { value: 'gpt-5', label: 'GPT-5' },
  { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
  { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
  { value: 'gpt-4.1', label: 'GPT-4.1' },
  { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
  { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'gpt-4', label: 'GPT-4' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' }
]

/**
 * AI models with "default" option (system setting)
 * Use for chat prompts where users can defer to system default
 */
export const AI_MODELS_WITH_DEFAULT: AIModelOption[] = [
  { value: 'default', label: 'デフォルト (システム設定)' },
  ...AI_MODELS
]

/**
 * Which `reasoning.effort` values each reasoning model accepts.
 *
 * The scale is not one enum across the GPT-5 series: `minimal` exists only on the
 * original gpt-5 models, `none` only on the later ones, and `xhigh`/`max` only on the
 * newest. OpenAI rejects the whole request for a value the model does not know
 * (400 unsupported_value), and because the effort is a single global admin setting a
 * wrong value takes down every chat on that model until someone changes it back -- so
 * the choices are per model rather than one shared list.
 *
 * The keys double as the list of reasoning models, which is why supportsReasoning reads
 * from here: a model added to the map can never end up offering neither knob.
 */
export const REASONING_EFFORTS_BY_MODEL: Record<string, string[]> = {
  'gpt-6-luna': ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
  'gpt-6-sol': ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
  'gpt-5.6-luna': ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
  'gpt-5.6-sol': ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
  'gpt-5.6-terra': ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
  'gpt-5.4': ['none', 'low', 'medium', 'high', 'xhigh'],
  'gpt-5.4-mini': ['none', 'low', 'medium', 'high', 'xhigh'],
  'gpt-5.4-nano': ['none', 'low', 'medium', 'high', 'xhigh'],
  'gpt-5.2': ['none', 'low', 'medium', 'high', 'xhigh'],
  'gpt-5.1': ['none', 'low', 'medium', 'high'],
  'gpt-5': ['minimal', 'low', 'medium', 'high'],
  'gpt-5-mini': ['minimal', 'low', 'medium', 'high'],
  'gpt-5-nano': ['minimal', 'low', 'medium', 'high'],
}

/** The `reasoning.effort` values this model accepts; empty for a non-reasoning model. */
export function reasoningEffortValues(model: string): string[] {
  return REASONING_EFFORTS_BY_MODEL[model] ?? []
}

/**
 * Reasoning models: the GPT-5 series. They take `reasoning.effort` and
 * `text.verbosity` in place of temperature, which is the only knob the older models
 * have.
 */
export function supportsReasoning(model: string): boolean {
  return reasoningEffortValues(model).length > 0
}

/**
 * Models that DO NOT support temperature parameter
 * GPT-5 series models don't support temperature according to OpenAI API
 */
export const MODELS_WITHOUT_TEMPERATURE_SUPPORT = Object.keys(REASONING_EFFORTS_BY_MODEL)

/**
 * Check if a model supports the temperature parameter
 */
export function supportsTemperature(model: string): boolean {
  return !MODELS_WITHOUT_TEMPERATURE_SUPPORT.includes(model)
}

/**
 * Models that predate Structured Outputs (`response_format: { type: 'json_schema' }`).
 * gpt-4-turbo still has the older `json_object` mode, which guarantees valid JSON but
 * not a particular shape; the original gpt-4 has neither and can only be asked in prose.
 */
const MODELS_WITHOUT_JSON_SCHEMA = ['gpt-4', 'gpt-4-turbo']
const MODELS_WITH_JSON_OBJECT_ONLY = ['gpt-4-turbo']

/**
 * The strongest `response_format` the given model accepts for a JSON reply, or null
 * when it accepts none and the prompt is the only thing holding the shape.
 *
 * Asking for the shape in prose alone is not enough: the grading prompts are assembled
 * from admin-authored criteria that carry their own output instructions, and a model
 * that follows those instead returns something JSON.parse rejects, which surfaces to
 * the student as a failed submission. Pinning the schema at the API level makes that
 * unrepresentable on every model new enough to support it.
 *
 * `schema` must satisfy the strict-mode rules: an object type, `additionalProperties:
 * false`, and every property listed in `required`.
 */
export function jsonResponseFormat(
  model: string,
  name: string,
  schema: Record<string, unknown>
): Record<string, unknown> | null {
  if (!MODELS_WITHOUT_JSON_SCHEMA.includes(model)) {
    return { type: 'json_schema', json_schema: { name, strict: true, schema } }
  }
  if (MODELS_WITH_JSON_OBJECT_ONLY.includes(model)) {
    return { type: 'json_object' }
  }
  return null
}

const REASONING_EFFORT_LABELS: Record<string, string> = {
  none: 'none（推論なし）',
  minimal: 'minimal（最小）',
  low: 'low（低）',
  medium: 'medium（中）',
  high: 'high（高）',
  xhigh: 'xhigh（非常に高）',
  max: 'max（最大）',
}

/**
 * How much the model thinks before answering. Lower means a faster, more literal
 * reply that sticks closer to the prompt; higher means more elaborate answers that
 * tend to run ahead of what was asked. Only the values the given model accepts are
 * offered, so the dropdown cannot produce a request OpenAI will reject.
 */
export function reasoningEffortOptions(model: string): AIModelOption[] {
  return [
    { value: '', label: 'モデル既定（送信しない）' },
    ...reasoningEffortValues(model).map(value => ({
      value,
      label: REASONING_EFFORT_LABELS[value] ?? value,
    })),
  ]
}

/**
 * How much the model writes. `low` keeps replies short, which suits a chat that is
 * meant to hand the thinking back to the student one step at a time.
 */
export const TEXT_VERBOSITY_OPTIONS: AIModelOption[] = [
  { value: '', label: 'モデル既定（送信しない）' },
  { value: 'low', label: 'low（簡潔）' },
  { value: 'medium', label: 'medium（標準）' },
  { value: 'high', label: 'high（詳細）' }
]
